import React, { useEffect } from 'react';
import { Box, ThemeProvider } from '@material-ui/core';
import { useParams } from 'react-router';
import MetaTags from 'react-meta-tags';

import { reportBugsnag } from '@/utils/helper';
import { pharmacyApi } from '@/api';
import { theme } from '@/theme';
import { useQuery } from '@/utils/hooks';

import { PharmacyOrder } from '@/types';
import { useSnackBar } from '@/store/snackbar/snackbarContext';
import { showErrorAlert } from '@/components/uikit';
import {
  ViewSuccess,
  ViewLoading,
  ViewError,
  ViewBiddingDone,
} from './Components';
import { useOrderPharmacy } from './hooks';

const ViewSuccessOrDone = ({
  code,
  data,
  updateOrderPharmacy,
}: {
  code: string | null;
  data: PharmacyOrder;
  updateOrderPharmacy: () => void;
}) => {
  if (data.status === 'order_cancelled') {
    return <ViewBiddingDone />;
  }

  // all apotek is bidding owner if it's not taken
  const pharmacyCode = data.pharmacy_code;
  const isBiddingOwner = pharmacyCode ? code === pharmacyCode : true;

  if (isBiddingOwner) {
    return (
      <ViewSuccess data={data} updateOrderPharmacy={updateOrderPharmacy} />
    );
  }

  return <ViewBiddingDone />;
};

export default function OrderPharmacy() {
  const code = useQuery().get('code');
  const { orderID } = useParams<{ orderID: string }>();
  const { order } = useOrderPharmacy(orderID, code);
  const { showSnackBar } = useSnackBar();

  const updateOrderPharmacy = () => {
    if (order.state !== 'success') return;

    if (!code) {
      reportBugsnag('code not found');
      return;
    }

    pharmacyApi
      .updatePharmacyOrder(orderID, {
        pharmacy_code: code,
        status: order.data.status,
      })
      .then(res => {
        if (res.status >= 200 && res.status < 300) {
          window.location.reload();
        }
      })
      .catch(err => {
        showSnackBar(
          err.message ?? err.data?.error ?? 'Unknown Error',
          'error'
        );
      });
  };

  useEffect(() => {
    if (
      order.state === 'success' &&
      order.data.status === 'order_processed' &&
      order.data.delivery.shipment_type.name === 'Same day' &&
      order.data.disable_preparing === true
    ) {
      showErrorAlert({
        message:
          "Button menyiapkan obat hanya aktif selama layanan Same Day: 09.00 - 15.00. Anda akan menerima reminder untuk klik tombol 'Selesai Menyiapkan Obat' saat layanan dimulai.",
        duration: 1000 * 60 * 8,
        title: 'Pesan',
      });
    }
  }, [order]);

  return (
    <ThemeProvider theme={theme}>
      <MetaTags>
        <meta name="description" content="Lifepack For Partner" />
        <title>Lifepack For Partner</title>
      </MetaTags>

      <Box bgcolor="#f7f9ff" minHeight="100vh" width="100%">
        {order.state === 'loading' && <ViewLoading />}

        {order.state === 'error' &&
          (order.code === 403 ? (
            <ViewBiddingDone />
          ) : (
            <ViewError message={order.message} />
          ))}

        {order.state === 'success' && (
          <ViewSuccessOrDone
            code={code}
            data={order.data}
            updateOrderPharmacy={updateOrderPharmacy}
          />
        )}
      </Box>
    </ThemeProvider>
  );
}
